// Training Sessions List — content (sans AppLayout), réutilisable en page ou en onglet
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Archive,
  Upload,
  Users,
  Calendar,
  Loader2,
  ExternalLink,
  Copy,
  Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Brouillon', variant: 'secondary', color: 'text-gray-500' },
  published: { label: 'Publiée', variant: 'default', color: 'text-green-600' },
  full: { label: 'Complète', variant: 'destructive', color: 'text-orange-500' },
  archived: { label: 'Archivée', variant: 'outline', color: 'text-gray-400' }
};

const currencySymbols = {
  EUR: '€',
  USD: '$',
  TND: 'TND'
};

export default function TrainingSessionsContent() {
  const { workspaceId: urlWorkspaceId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const initDefaults = async () => {
    try {
      // Namespace admin (pas de workspace) — géré côté backend
      await fetch(`${API_URL}/api/training/init-defaults`, {
        method: 'POST',
        headers: authHeaders
      });
    } catch (e) {
      console.log('Init defaults:', e);
    }
  };

  const loadData = async () => {
    try {
      // Sessions rattachées au dashboard admin (pas de workspace) => toutes
      let url = `${API_URL}/api/training/sessions?`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterType !== 'all') url += `&course_type_id=${filterType}`;

      const sessionsRes = await fetch(url, { headers: authHeaders });
      const typesRes = await fetch(`${API_URL}/api/training/session-types`, { headers: authHeaders });

      if (sessionsRes.ok) setSessions(await sessionsRes.json());
      if (typesRes.ok) setSessionTypes(await typesRes.json());
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initDefaults();
    loadData();
    // eslint-disable-next-line
  }, [filterStatus, filterType]);

  const handleDelete = async (session) => {
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${session.id}`,
        {
          method: 'DELETE',
          headers: authHeaders
        }
      );

      if (response.ok) {
        toast.success('Session supprimée');
        loadData();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handlePublish = async (session) => {
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${session.id}/publish`,
        {
          method: 'POST',
          headers: authHeaders
        }
      );

      if (response.ok) {
        toast.success('Session publiée');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleArchive = async (session) => {
    try {
      const response = await fetch(
        `${API_URL}/api/training/sessions/${session.id}/archive`,
        {
          method: 'POST',
          headers: authHeaders
        }
      );

      if (response.ok) {
        toast.success('Session archivée');
        loadData();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const copyRegistrationLink = (session) => {
    const url = `${window.location.origin}/sessions/${session.slug}/inscription`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatPrice = (session) => {
    const currency = session.primary_currency;
    const price = session.prices?.[currency] || 0;
    const symbol = currencySymbols[currency];
    return `${price} ${symbol}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  // Determine navigation base path
  const basePath = urlWorkspaceId ? `/workspace/${urlWorkspaceId}/training` : '/training';

  return (
    <>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Sessions de Formation</h1>
            <p className="text-muted-foreground">
              Gérez vos sessions et les inscriptions
            </p>
          </div>
          <Button onClick={() => navigate(`${basePath}/new`)}>
            <Plus className="h-4 w-4 mr-2" />
            Créer une session
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Filtres:</span>
              </div>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="published">Publiée</SelectItem>
                  <SelectItem value="full">Complète</SelectItem>
                  <SelectItem value="archived">Archivée</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Type de cours" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {sessionTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Sessions Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Session</TableHead>
                <TableHead>Période</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-center">Places</TableHead>
                <TableHead className="text-right">Prix</TableHead>
                <TableHead className="text-center">Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucune session de formation. Créez votre première session !
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{session.name}</p>
                        {session.module_names?.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {session.module_names.slice(0, 2).join(', ')}
                            {session.module_names.length > 2 && ` +${session.module_names.length - 2}`}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {formatDate(session.start_date)} - {formatDate(session.end_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{session.course_type_name}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Users className="h-3 w-3 text-muted-foreground" />
                        <span className={session.registrations_count >= session.max_candidates ? 'text-orange-500 font-medium' : ''}>
                          {session.registrations_count}/{session.max_candidates}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatPrice(session)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant={statusConfig[session.status]?.variant || 'secondary'}
                        className={statusConfig[session.status]?.color}
                      >
                        {statusConfig[session.status]?.label || session.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`${basePath}/${session.id}`)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`${basePath}/${session.id}?tab=inscriptions`)}>
                            <Users className="h-4 w-4 mr-2" />
                            Voir inscriptions
                          </DropdownMenuItem>
                          {session.status === 'published' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => copyRegistrationLink(session)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Copier lien inscription
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => window.open(`/sessions/${session.slug}/inscription`, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Voir page publique
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          {session.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handlePublish(session)}>
                              <Upload className="h-4 w-4 mr-2" />
                              Publier
                            </DropdownMenuItem>
                          )}
                          {session.status !== 'archived' && (
                            <DropdownMenuItem onClick={() => handleArchive(session)}>
                              <Archive className="h-4 w-4 mr-2" />
                              Archiver
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteConfirm(session)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la session ?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Cette action supprimera définitivement la session "{deleteConfirm?.name}" et toutes ses inscriptions.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
